import React, { useEffect, useState } from 'react';
import { api, API_BASE_URL } from '../api'; // Import from local api
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosResponse, AxiosError } from 'axios';

// Placeholder for TwitchIcon, assuming it's available or create a simple one
const TwitchIcon = () => <img src="/assets/twitchicon.svg" alt="Twitch" className="w-5 h-5 mr-2" />;

// Re-using interfaces from AccountSettingsPage.tsx for consistency if possible,
// or define them here if they are specific or slightly different.
// For /api/auth/me response:
interface ApiUser { // Describes the user object from the API (e.g., /api/auth/me)
  userId: string;
  email: string;
  username: string;
  role: string;
  name?: string;
  googleId?: string;
  twitchId?: string;
  profileImageUrl?: string;
  firstLoginProvider?: 'GOOGLE' | 'TWITCH' | 'EMAIL' | string;
}

interface AuthMeApiResponseData { // Describes the response structure for /api/auth/me
  success: boolean;
  user: ApiUser;
  message?: string;
}

interface FrontendUser { // Describes the user structure for the frontend context
  id: number;
  email: string;
  username: string;
  role: 'USER' | 'ADMIN';
  name?: string;
  googleId?: string | null;
  twitchId?: string | null;
  profileImageUrl?: string | null;
  firstLoginProvider?: string | null;
}

interface OAuthURLResponseData { // For /api/auth/twitch response
    success: boolean;
    url?: string;
    message?: string;
}

interface GeneralApiResponse { // For generic responses
    success: boolean;
    message?: string;
}


// Define a type for the VOD data received from Twitch API
interface TwitchVod {
  id: string;
  user_id: string;
  user_login: string;
  user_name: string;
  title: string;
  description: string;
  created_at: string;
  published_at: string;
  url: string;
  thumbnail_url: string;
  viewable: string;
  view_count: number;
  language: string;
  type: 'archive' | 'highlight' | 'upload'; // 'archive' is typically a full VOD
  duration: string; // e.g., "3h8m33s"
  muted_segments: Array<{ duration: number; offset: number }> | null;
}

const MyVodsPage: React.FC = () => {
  const [vods, setVods] = useState<TwitchVod[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user, setUser } = useAuth(); // Get user and setUser from AuthContext
  const location = useLocation();
  const navigate = useNavigate();

  const [isConnectingOAuth, setIsConnectingOAuth] = useState<null | 'twitch'>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; text: string } | null>(null);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linkSuccess = params.get('link_success');
    const linkError = params.get('link_error');
    const message = params.get('message');
    const errorMessage = params.get('error_message');

    if (linkSuccess === 'twitch') {
      setNotification({ type: 'success', text: message || `Twitch account linked successfully!` });
      // Refresh user data to reflect the new link
      api.get('/auth/me').then((response: AxiosResponse<AuthMeApiResponseData>) => {
        if (response.data.success && response.data.user && setUser) {
          const apiUser = response.data.user;
          const frontendUser: FrontendUser = {
            id: parseInt(apiUser.userId, 10),
            email: apiUser.email,
            username: apiUser.username,
            role: apiUser.role as 'USER' | 'ADMIN',
            name: apiUser.name,
            googleId: apiUser.googleId,
            twitchId: apiUser.twitchId,
            profileImageUrl: apiUser.profileImageUrl,
            firstLoginProvider: apiUser.firstLoginProvider,
          };
          setUser(frontendUser);
        }
      }).catch((err: AxiosError | any) => console.error("Failed to refresh user after linking Twitch:", err.response?.data || err.message));
      navigate(location.pathname, { replace: true }); // Clear URL params
    }
    if (linkError === 'twitch') {
      setNotification({ type: 'error', text: errorMessage || `Failed to link Twitch account. Please try again.` });
      navigate(location.pathname, { replace: true }); // Clear URL params
    }
  }, [location, navigate, setUser]);


  useEffect(() => {
    const fetchVods = async () => {
      if (!user?.twitchId) {
        setError("Please link your Twitch account to see your VODs.");
        setLoading(false);
        setVods([]); // Clear VODs if Twitch is not linked
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await api.get<{ success: boolean, data: TwitchVod[], message?: string }>(
          `/user/twitch-vods`
        );

        if (response.data.success) {
          setVods(response.data.data);
        } else {
          setError(response.data.message || 'Failed to fetch VODs.');
        }
      } catch (error) {
        const err = error as AxiosError<{ message?: string, success?: boolean, error_code?: string }>;
        console.error('Error fetching VODs:', err);
        
        // Handle the specific 409 Conflict for invalid Twitch tokens
        if (err.response && err.response.status === 409 && err.response.data?.error_code === 'TWITCH_TOKEN_INVALID') {
            setError(err.response.data.message || "Twitch authentication is invalid. Please re-link your account.");
            setVods([]);
        } else if (err.response && err.response.status === 404) {
            // Handle case where Twitch account is not linked at all
            setError("Your Twitch account is not linked. Please link your account to see your VODs.");
            setVods([]);
        } else if (err.response && err.response.data && err.response.data.message) {
          setError(err.response.data.message);
        } else {
          setError('An unexpected error occurred while fetching VODs.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) { // Only fetch VODs if user data is available
        fetchVods();
    } else {
        // If user is null (still loading from AuthContext or not logged in),
        // you might want to set loading to false or handle appropriately.
        // For now, we assume AuthContext handles the initial loading state of user.
        // If user is not logged in, fetchVods won't run due to !user?.twitchId check.
    }
  }, [user]); // Depend on user to refetch if user logs in/out or links Twitch

  const handleConnectTwitch = async () => {
    if (isConnectingOAuth) return;
    setIsConnectingOAuth('twitch');
    setNotification(null);
    try {
      const response: AxiosResponse<OAuthURLResponseData> = await api.get(`/auth/twitch`);
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        console.error(`Failed to get Twitch OAuth URL:`, response.data.message);
        setNotification({ type: 'error', text: `Could not initiate Twitch connection. ${response.data.message || 'Please try again.'}` });
        setIsConnectingOAuth(null);
      }
    } catch (error) {
      const axiosError = error as AxiosError<GeneralApiResponse>;
      console.error(`Error connecting with Twitch:`, axiosError);
      setNotification({ type: 'error', text: `An error occurred while trying to connect with Twitch. ${axiosError.response?.data?.message || axiosError.message || 'Please try again.'}` });
      setIsConnectingOAuth(null);
    }
  };


  if (loading && !error) { // Show loading only if no error has occurred yet
    return (
      <div className="container mx-auto p-4 text-center">
        <p>Loading VODs...</p>
        {/* You can add a spinner here */}
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center text-red-500">
        <p>Error: {error}</p>
        {!user?.twitchId && (
          <div className="mt-4">
            <button
              onClick={handleConnectTwitch}
              disabled={!!isConnectingOAuth}
              className="px-4 py-2 bg-[#9146FF] hover:bg-[#772CE8] text-white text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9146FF]/50 flex items-center justify-center mx-auto disabled:opacity-50"
            >
              {isConnectingOAuth === 'twitch' ? (
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <TwitchIcon />
              )}
              {isConnectingOAuth === 'twitch' ? 'Connecting...' : 'Connect with Twitch'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      {notification && (
        <div
          className={`p-4 mb-4 text-sm rounded-lg ${
            notification.type === 'success' ? 'bg-green-100 text-green-700 border border-green-400' : 'bg-red-100 text-red-700 border border-red-400'
          }`}
          role="alert"
        >
          <span className="font-medium">{notification.type === 'success' ? 'Success!' : 'Error!'}</span> {notification.text}
          <button onClick={() => setNotification(null)} className="float-right font-bold">X</button>
        </div>
      )}
      <h1 className="text-3xl font-bold mb-6 text-foreground">Twitch VODs</h1>
      {!user?.twitchId && !loading && ( // Show if not linked, not loading, and no overriding error message already showing the button
         error !== "Please link your Twitch account to see your VODs." && // Avoid double button if error already suggests linking
         error !== "Twitch account not linked or token invalid. Please link your Twitch account." &&
        <div className="mb-6 p-4 bg-card border border-border rounded-lg shadow text-center">
          <p className="text-muted-foreground mb-4">
            To see your VODs, please link your Twitch account.
          </p>
          <button
            onClick={handleConnectTwitch}
            disabled={!!isConnectingOAuth}
            className="px-4 py-2 bg-[#9146FF] hover:bg-[#772CE8] text-white text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9146FF]/50 flex items-center justify-center mx-auto disabled:opacity-50"
          >
            {isConnectingOAuth === 'twitch' ? (
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <TwitchIcon />
            )}
            {isConnectingOAuth === 'twitch' ? 'Connecting...' : 'Connect with Twitch'}
          </button>
        </div>
      )}

      {user?.twitchId && vods.length === 0 && !loading && !error && (
        <p className="text-muted-foreground">No VODs found. Make sure your Twitch account is linked and has past broadcasts.</p>
      )}

      {user?.twitchId && vods.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {vods.map((vod) => (
            <div key={vod.id} className="bg-card border border-border rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 duration-300 ease-in-out">
              <a href={vod.url} target="_blank" rel="noopener noreferrer">
                <img
                  src={vod.thumbnail_url.replace('%{width}', '440').replace('%{height}', '248')}
                  alt={vod.title}
                  className="w-full h-48 object-cover"
                />
              </a>
              <div className="p-4">
                <h2 className="text-lg font-semibold text-foreground mb-1 truncate" title={vod.title}>{vod.title}</h2>
                <p className="text-sm text-muted-foreground mb-1">By: {vod.user_name}</p>
                <p className="text-xs text-muted-foreground mb-2">Duration: {vod.duration}</p>
                <p className="text-xs text-muted-foreground">Views: {vod.view_count.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Published: {new Date(vod.published_at).toLocaleDateString()}</p>
                {/* TODO: Implement VOD card similar to ClipCard */}
                {/* TODO: Implement popup video player for VODs */}
                 <a
                  href={vod.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-block bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Watch on Twitch
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyVodsPage;